// THE LARGE MODAL'S SIZE, in one place. The invoice detail modal overlays the
// page's content area: top just under the header's blue line, bottom near the
// viewport edge, width matching the page content box. App.tsx tags the anchors;
// lib/overlay.ts measures the live layout into the --ip-overlay-* CSS variables
// (the fallbacks approximate the same geometry before they are set). The 8px
// insets keep the overlay clearly in frame rather than pixel-flush (user
// preference).
//
// Shared since 2026-10-07, when the owner asked for the Notifications dialogs to open
// "as large as the invoice modal": a second copy of these numbers would drift
// from the first the next time either changed.

import { useLayoutEffect } from 'react'
import type { GriffelStyle } from '@fluentui/react-components'
import { trackOverlayVars } from '../lib/overlay'

/** The `DialogSurface` style. Spread it into a `makeStyles` entry. */
export const OVERLAY_SURFACE_STYLE: GriffelStyle = {
  position: 'fixed',
  top: 'calc(var(--ip-overlay-top, 72px) + 8px)',
  bottom: '8px',
  left: 'var(--ip-overlay-left, 3vw)',
  width: 'var(--ip-overlay-width, 94vw)',
  maxWidth: 'none',
  // Fluent's surface defaults to height: fit-content, which ignores the
  // top/bottom anchors and overflows the viewport — auto restores anchoring.
  height: 'auto',
  maxHeight: 'none',
  margin: '0',
}

/** The `DialogBody` style: fill the now fixed-height surface; inner panes own the scrolling. */
export const OVERLAY_BODY_STYLE: GriffelStyle = { height: '100%', maxHeight: 'none' }

/**
 * Measure the overlay geometry as the modal opens (before first paint) and keep
 * it fresh while open — see lib/overlay.ts. Also lock the page scroll: the
 * overlay is position:fixed against measured anchors, so background scrolling
 * shifts its contents — and there is nothing to scroll to while a modal is up
 * anyway. (The re-measure-on-scroll tracking stays, as a belt for scrolls we
 * cannot lock, e.g. the host page outside the app frame.)
 */
export function useOverlayGeometry(open: boolean): void {
  useLayoutEffect(() => {
    if (!open) return
    const html = document.documentElement
    const body = document.body
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = body.style.overflow
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    const untrack = trackOverlayVars()
    return () => {
      html.style.overflow = prevHtmlOverflow
      body.style.overflow = prevBodyOverflow
      untrack()
    }
  }, [open])
}
